import { CommonModule } from '@angular/common';
import { Component, ElementRef, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import { createSelector, Store } from '@ngrx/store';
import { QuillTemplateLocation } from 'graphql.generated';
import { QuillEditorComponent } from 'ngx-quill';
import Quill from 'quill';
import { map, switchMap, take, tap } from 'rxjs';
import { ArtifactService } from 'src/app/core/public-api';
import { FreyaNotificationsService } from 'src/app/services/freya-notifications.service';

import { QuillTemplateVariableToolbarDirective } from 'src/app/shared/quill-template-variable.directive';
import { generateUUID } from 'src/app/utilities/state.util';

import { SubSink } from 'subsink';

import { FreyaCommonModule } from '../../../freya-common/freya-common.module';
import { COMMENT_MODES_TEMPLATES } from '../../../shared/quill-modules/quill-templates.helpers';
import { brandingFeature } from '../../../state/branding.store';
import { JobCreateCustomerActions } from '../../job-create/job-create-customer/job-create-customer.actions';
import { jobToolFeature } from '../../job-tool.reducer';
import { commentDefaultToolbar } from '../comments.utils';
import { JobActivityActions } from '../job-activity.actions';
import { PendingFile } from '../job-activity.state';
import { defaultCommentMode } from '../job-comment/job-comment.component';
import { JobCommentArtifactHandlerService } from '../job-comment-artifact-handler.service';
import { AttachmentToolbarQuillModule } from '../quill-modules/attachmentToolbar.quillModule';
import { CameraCaptureQuillModule } from '../quill-modules/cameraCapture.quillModule';
import { ImageUploadQuillModule } from '../quill-modules/imageUpload.quillModule';
import { TemplateToolbarQuillModule } from '../quill-modules/templateToolbar.quillModule';

@Component({
  selector: 'app-job-add-comment',
  standalone: true,
  imports: [
    CommonModule,
    QuillEditorComponent,
    FormsModule,
    FreyaCommonModule,
    QuillTemplateVariableToolbarDirective,
  ],
  templateUrl: './job-add-comment.component.html',
  styleUrl: './job-add-comment.component.scss'
})
export class JobAddCommentComponent implements OnDestroy {

  @ViewChild('addCommentContainer', { static: true }) addCommentContainer: ElementRef<HTMLElement> | undefined;

  private subs = new SubSink();
  private intersectionObserver: IntersectionObserver | undefined;
  private attachmentModule: AttachmentToolbarQuillModule | undefined;
  private cameraCaptureModule: CameraCaptureQuillModule | undefined;
  private imageUploadModule: ImageUploadQuillModule | undefined;
  private templateToolbarModule: TemplateToolbarQuillModule | undefined;

  public variablesLocation = QuillTemplateLocation.GenericJobComment;

  viewModel$ = this.store.select(createSelector(
    jobToolFeature.selectAddComment,
    jobToolFeature.selectCommentsMutating,
    brandingFeature.selectCommentModes,
    (
      { subject, contents, commentModeId, pendingFiles },
      mutating,
      commentModes,
    ) => {

      if (!commentModes?.length) {
        commentModes = [ defaultCommentMode ];
      }

      const mode = commentModes.find((m) => m.id === commentModeId);

      const commentModeMenuItems = commentModes
        .filter((m) => !m?.hidden)
        .map((m) => ({
          ...m,
          label: m.name,
          visible: m.id !== commentModeId,
          tooltip: m.description,
          tooltipOptions: {
            tooltipPosition: 'left',
          },
          // disabled: allDisabled,
          command: () => this.store.dispatch(this.actions.addCommentModeSelected({ commentModeId: m.id })),
        }));

      return {
        contents,
        subject,
        mutating,
        commentModes,
        pendingFiles,
        templatesLocations: [COMMENT_MODES_TEMPLATES[commentModeId]],
        currentCommentMode: mode || defaultCommentMode,
        commentModeMenuItems,
      }
    }
  ));

  public actions = JobActivityActions;

  public modules = {
    toolbar: commentDefaultToolbar,
    variableToolbar: true,
    templateToolbar: true,
    attachmentToolbar: true,
    imageUpload: true,
    cameraCapture: true,
    keyboard: {
      bindings: {
        submit: {
          key: 'Enter',
          ctrlKey: true,
          handler: (_range, _context) => {
            this.store.dispatch(this.actions.addCommentCtrlEnterPressed());
          },
        },
      },
    },
  };

  public subjectModules = {
    toolbar: false,
    variableToolbar: { hideVariableButton: true },
  };

  error: string;

  constructor(
    public store: Store,
    private actions$: Actions,
    private ngZone: NgZone,
    private artifactHandler: JobCommentArtifactHandlerService,
    private artifactService: ArtifactService,
    private notifications: FreyaNotificationsService,
  ) { }

  onContentEditorCreated(editor: Quill): void {
    this.templateToolbarModule = editor.getModule('templateToolbar') as TemplateToolbarQuillModule;
    if (this.templateToolbarModule) {
      this.templateToolbarModule.onTemplateSelected = (template) => {
        this.ngZone.run(() => this.store.dispatch(this.actions.addCommentTemplateSelected({ template })));
      };
    }

    this.attachmentModule = editor.getModule('attachmentToolbar') as AttachmentToolbarQuillModule;
    if (this.attachmentModule) {
      this.attachmentModule.onFilesRejected = (rejected) => {
        this.store.dispatch(this.actions.addCommentFilesRejected({
          rejected: rejected.map((r) => ({ name: r.file.name, reason: r.reason })),
        }));
      };

      this.attachmentModule.onFilesSelected = (files) => {
        const pendingFiles: PendingFile[] = [];
        for (const file of files) {
          const id = generateUUID();
          this.artifactHandler.holdFile(id, file);
          pendingFiles.push({ id, name: file.name, size: file.size, type: file.type });
        }
        this.store.dispatch(this.actions.addCommentFilesSelected({ files: pendingFiles }));
      };
    }

    this.imageUploadModule = editor.getModule('imageUpload') as ImageUploadQuillModule;
    if (this.imageUploadModule) {
      this.imageUploadModule.onImageRejected = (file, reason) => {
        const detail = reason === 'size'
          ? `"${file.name}" exceeds 10 MB limit`
          : `"${file.name}" is not a supported image type`;
        this.notifications.addToast.next({ severity: 'error', summary: 'Image not allowed', detail });
      };

      this.imageUploadModule.onImageSelected = (file, insertImage) => {
        this.uploadAndInsertImage(file, insertImage);
      };
    }

    this.cameraCaptureModule = editor.getModule('cameraCapture') as CameraCaptureQuillModule;
    if (this.cameraCaptureModule) {
      this.cameraCaptureModule.onImageRejected = (file, reason) => {
        const detail = reason === 'size'
          ? `"${file.name}" exceeds 10 MB limit`
          : `"${file.name}" is not a supported image type`;
        this.notifications.addToast.next({ severity: 'error', summary: 'Image not allowed', detail });
      };

      this.cameraCaptureModule.onImageSelected = (file, insertImage) => {
        this.uploadAndInsertImage(file, insertImage);
      };
    }
  }

  private uploadAndInsertImage(file: File, insertImage: (url: string) => void): void {
    this.subs.sink = this.artifactService.createArtifact([{
      name: file.name,
      contentType: file.type,
      fileSize: file.size,
      namespace: 'job-comment-inline-image',
      public: true,
    }]).pipe(
      switchMap((result) => {
        const artifact = result.data.createArtifacts.artifacts[0];
        if (!artifact) {
          throw new Error('No artifact returned from server');
        }
        const policy = JSON.parse(artifact.signedPolicy);
        return this.artifactService.uploadFile(file as any, policy).pipe(
          map(() => artifact.url),
        );
      }),
    ).subscribe({
      next: (url) => insertImage(url),
      error: () => {
        this.notifications.addToast.next({
          severity: 'error',
          summary: 'Image upload failed',
          detail: `Could not upload "${file.name}"`,
        });
      },
    });
  }

  setUpScrollToEditor(editor: Quill): void {
    this.subs.sink = this.actions$.pipe(
      ofType(JobCreateCustomerActions.sendMessageToCustomer),
      switchMap(() => this.ngZone.onStable.pipe(
        take(1),
        tap(() => {
          // Disconnect any previous observer before creating a new one
          if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
          }

          editor.container.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });

          if (!('IntersectionObserver' in window)) {
            editor.focus();
          } else {
            this.intersectionObserver = new IntersectionObserver(
              ([ entry ], _obs) => {
                if (entry.isIntersecting) {
                  editor.focus();
                  this.intersectionObserver.disconnect();
                }
              },
              { root: null, threshold: 0.8 },
            );
            this.intersectionObserver.observe(editor.container);
          }
        }),
      )),
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    this.attachmentModule?.destroy();
    this.cameraCaptureModule?.destroy();
    this.imageUploadModule?.destroy();
    if (this.templateToolbarModule) {
      this.templateToolbarModule.onTemplateSelected = undefined;
    }
  }

}
